import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import {
  parseExceptionPeriods,
  type ClinicHoursException,
  type ClinicHoursExceptionType,
  type HourPeriod,
} from '@/lib/clinicHours';
import type { Json } from '@/types/database.generated';

export const clinicHoursExceptionsQueryKey = (
  clinicId: string | null | undefined,
  from: string | null,
  to: string | null,
) => ['clinic-hours-exceptions', clinicId ?? null, from, to] as const;

function readException(row: Record<string, unknown>): ClinicHoursException {
  return {
    id: String(row.id),
    clinic_id: String(row.clinic_id),
    exception_date: String(row.exception_date),
    exception_type: row.exception_type === 'closed' ? 'closed' : 'custom_hours',
    periods: parseExceptionPeriods(row.periods),
    created_by: (row.created_by as string | null) ?? null,
    created_at: row.created_at as string | undefined,
    updated_at: row.updated_at as string | undefined,
  };
}

export function useClinicHoursExceptions(
  clinicId: string | null | undefined,
  from: string | null,
  to: string | null,
) {
  return useQuery({
    queryKey: clinicHoursExceptionsQueryKey(clinicId, from, to),
    enabled: Boolean(clinicId && from && to),
    queryFn: async (): Promise<ClinicHoursException[]> => {
      if (!clinicId || !from || !to) return [];
      const { data, error } = await supabase
        .from('clinic_hours_exceptions')
        .select('id, clinic_id, exception_date, exception_type, periods, created_by, created_at, updated_at')
        .eq('clinic_id', clinicId)
        .gte('exception_date', from)
        .lte('exception_date', to)
        .order('exception_date');
      if (error) throw error;
      return (data ?? []).map((row) => readException(row as Record<string, unknown>));
    },
    staleTime: 30_000,
  });
}

export function useUpsertClinicHoursException(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      date: string;
      exceptionType: ClinicHoursExceptionType;
      periods?: HourPeriod[];
    }): Promise<{ exception: ClinicHoursException; existingAppointments: number }> => {
      if (!clinicId) throw new Error('No active clinic');
      const { data, error } = await supabase.rpc('upsert_clinic_hours_exception', {
        p_clinic_id: clinicId,
        p_date: input.date,
        p_exception_type: input.exceptionType,
        p_periods: (input.periods ?? []) as unknown as Json,
      });
      if (error) throw error;
      const payload = data as {
        exception: Record<string, unknown>;
        existing_appointments: number;
      };
      return {
        exception: readException(payload.exception),
        existingAppointments: payload.existing_appointments ?? 0,
      };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clinic-hours-exceptions', clinicId ?? null] });
      void qc.invalidateQueries({ queryKey: ['public-booking'] });
    },
  });
}

export function useDeleteClinicHoursException(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (date: string): Promise<boolean> => {
      if (!clinicId) throw new Error('No active clinic');
      const { data, error } = await supabase.rpc('delete_clinic_hours_exception', {
        p_clinic_id: clinicId,
        p_date: date,
      });
      if (error) throw error;
      return Boolean(data);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clinic-hours-exceptions', clinicId ?? null] });
      void qc.invalidateQueries({ queryKey: ['public-booking'] });
    },
  });
}

export function useCountAppointmentsOnDate(clinicId: string | null | undefined, date: string | null) {
  return useQuery({
    queryKey: ['clinic-hours-appointment-count', clinicId ?? null, date],
    enabled: Boolean(clinicId && date),
    queryFn: async (): Promise<number> => {
      if (!clinicId || !date) return 0;
      const { data, error } = await supabase.rpc('count_clinic_appointments_on_date', {
        p_clinic_id: clinicId,
        p_date: date,
      });
      if (error) throw error;
      return Number(data ?? 0);
    },
  });
}
